import { useCallback, useState } from 'react'
import type { StartWorkItemDto } from '@myapp/contract'
import type { PrimaryWorkAction } from './workActionPolicy'
import { nowInstant } from './workSessionTime'

/** The actions that are a pure tap. The two closing actions open a sheet instead. */
export type TapAction = Extract<PrimaryWorkAction, 'start-travel' | 'start'>

interface Runners {
  /** `body` only when confirming a start over a rescheduling flag — see `useStartGate`. */
  'start-travel'?: (body?: StartWorkItemDto) => Promise<unknown>
  start: (body?: StartWorkItemDto) => Promise<unknown>
  /** `Rückgängig` — clears the last stamped instant. Omit where undoing is not offered. */
  undo?: () => Promise<unknown>
}

export interface PhaseTaps {
  run: (action: TapAction, body?: StartWorkItemDto) => void
  undo: (() => void) | undefined
  isPending: boolean
  /** The last tap did not reach the server. The action bar must keep saying so. */
  failed: boolean
  /**
   * What the last failed tap threw, until the next tap. Kept for the one caller that has to tell
   * failures apart — `useStartGate`, which turns a rescheduling 409 into a dialog.
   */
  error: unknown
  /** Set when a tap landed, so the bar can confirm it and offer `Rückgängig`. */
  confirmedAt: string | null
}

/**
 * The tap half of the worker flow: fire the mutation, then own the three outcomes the action bar
 * renders — in flight, landed (with an undo window), or failed.
 *
 * The failure state is the load-bearing one. The worker has no backfill: a tap that silently
 * fails leaves him believing the session is open, and only the office can repair that. So a
 * failure is sticky until a retry succeeds, and never auto-dismisses.
 */
export function usePhaseTaps(runners: Runners): PhaseTaps {
  const [isPending, setIsPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null)

  const execute = useCallback(async (run: () => Promise<unknown>) => {
    setIsPending(true)
    setFailed(false)
    setError(null)
    try {
      await run()
      setConfirmedAt(nowInstant())
    } catch (caught) {
      // The bar only needs to know that this did not land; the error is kept for the gate.
      setFailed(true)
      setError(caught)
    } finally {
      setIsPending(false)
    }
  }, [])

  const run = useCallback(
    (action: TapAction, body?: StartWorkItemDto) => {
      const runner = runners[action]
      if (!runner) return
      void execute(() => runner(body))
    },
    // The runners are fresh closures each render; only the executor identity matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [execute],
  )

  const undo = useCallback(() => {
    if (!runners.undo) return
    setConfirmedAt(null)
    void execute(runners.undo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute])

  return {
    run,
    undo: runners.undo ? undo : undefined,
    isPending,
    failed,
    error,
    confirmedAt,
  }
}
